import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as previsioniApi from "api/pianificazione/previsioniApi";
import { toast } from "react-toastify";
// Import filtri da userPreference
import { FiltriPrevisioni } from "components/userPreference/userPreferenceSlice";

// ============================================
// INTERFACES
// ============================================

// Enum per stati e unità misura
export const UNITA_MISURA_PREVISIONI = {
    PZ: "Pezzi",
    CA: "Capo",
    PA: "Paio",
    MT: "Metri",
} as const;

export const FASE_COLLEZIONE = {
    PROT: "Prototipo",
    CAMP: "Campione",
    AN: "Annullato",
} as const;


export interface Previsione {
    id?: number;
    codice_item?: string;
    descrizione_item?: string;
    articolo_cliente?: string;
    unita_misura?: string;
    unita_misura_display?: string;
    collezione?: string;

    // Foreign keys - solo ID
    mandatario?: number;
    mandatario_display?: string;
    brand?: number;
    brand_display?: string;
    confezionista?: number;
    confezionista_display?: string;
    stagione?: number;
    stagione_display?: string;
    commerciale?: number;
    commerciale_display?: string;
    tecnica?: number;
    tecnica_display?: string;

    fase_collezione?: string;
    fase_collezione_display?: string;

    // Campi numerici
    px_prod?: number | string;  // Decimal può arrivare come string
    impianto?: number | string;
    px_camp?: number | string;
    qta_bdg?: number | string;

    note?: string;
    errorsStack?: ErrorsStack;
}

export interface PrevisioniList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: Previsione[];
    errorsStack: ErrorsStack;
}

// ============================================
// STATO SLICE
// ============================================

export interface PrevisioniState {
    previsioniList: PrevisioniList;
    previsioneCorrenteId?: number;  // SOLO ID, mai oggetto completo
}

const initialState: PrevisioniState = {
    previsioniList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    previsioneCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchPrevisioni = createAsyncThunk(
    "previsioni/fetchPrevisioni",
    async (parametri: FiltriPrevisioni = {}) => {
        return await previsioniApi.fetchPrevisioni(parametri);
    }
);

// Fetch TUTTE le previsioni (senza paginazione) per generazione PDF
export const fetchPrevisioniTutte = createAsyncThunk(
    "previsioni/fetchPrevisioniTutte",
    async (parametri: FiltriPrevisioni = {}) => {
        return await previsioniApi.fetchPrevisioni({...parametri, tutti: true});
    }
);

export const getPrevisione = createAsyncThunk(
    "previsioni/getPrevisione",
    async (parametri: { previsione_id: number }) => {
        return await previsioniApi.getPrevisione(parametri.previsione_id);
    }
);

export const savePrevisione = createAsyncThunk(
    "previsioni/savePrevisione",
    async (parametri: { previsioneToSave: Previsione, azione?:string }) => {
        return await previsioniApi.savePrevisione(parametri.previsioneToSave, parametri.azione);
    }
);

export const deletePrevisione = createAsyncThunk(
    "previsioni/deletePrevisione",
    async (parametri: { previsioneToDelete: Previsione }) => {
        return await previsioniApi.deletePrevisione(parametri.previsioneToDelete);
    }
);

// ============================================
// SLICE
// ============================================

export const previsioniSlice = createSlice({
    name: "previsioni",
    initialState,
    reducers: {
        setPrevisioneCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.previsioneCorrenteId = action.payload;
        },
        resetPrevisioni: (state) => {
            state.previsioneCorrenteId = undefined;
            state.previsioniList = initialState.previsioniList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchPrevisioni.pending, (state) => {
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchPrevisioni.fulfilled, (state, action) => {
            state.previsioniList = action.payload;
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchPrevisioni.rejected, (state, action) => {
            state.previsioniList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // GET (Singolo)
        // ============================================
        builder.addCase(getPrevisione.pending, (state) => {
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(getPrevisione.fulfilled, (state, action) => {
            // Rimuove versione esistente
            state.previsioniList.results = state.previsioniList.results.filter(
                (item) => item.id !== action.payload.id
            );
            // Aggiunge versione aggiornata
            state.previsioniList.results.push(action.payload);
            // Ordina per id
            state.previsioniList.results.sort((a, b) => (a.id || 0) - (b.id || 0));

            state.previsioneCorrenteId = action.payload.id;
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(getPrevisione.rejected, (state, action) => {
            state.previsioniList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // SAVE (Create/Update)
        // ============================================
        builder.addCase(savePrevisione.pending, (state) => {
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(savePrevisione.fulfilled, (state, action) => {
            const index = state.previsioniList.results.findIndex(
                (item) => item.id === action.payload.id
            );

            if (index !== -1) {
                // Update esistente
                state.previsioniList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                };
            } else {
                // Nuovo elemento
                state.previsioniList.results.unshift({
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.SUCCESS },
                });
                state.previsioniList.count++;
            }

            // Ordina per id
            state.previsioniList.results.sort((a, b) => (a.id || 0) - (b.id || 0));
            state.previsioneCorrenteId = action.payload.id;
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.SUCCESS };

            // NO toast.success() se usa AutoSaveIndicator nel form!
        });
        builder.addCase(savePrevisione.rejected, (state, action) => {
            // Aggiorna errore sull'elemento specifico
            state.previsioniList.results = state.previsioniList.results.map((item) => {
                if (item.id === action.meta.arg.previsioneToSave.id) {
                    return {
                        ...item,
                        errorsStack: parseErrorMessage(action.error),
                    };
                }
                return item;
            });
            state.previsioniList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // DELETE
        // ============================================
        builder.addCase(deletePrevisione.pending, (state) => {
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(deletePrevisione.fulfilled, (state, action) => {
            state.previsioniList.results = state.previsioniList.results.filter(
                (item) => item.id !== action.meta.arg.previsioneToDelete.id
            );
            state.previsioniList.count--;
            state.previsioniList.errorsStack = { status: ErrorStatusTypes.OK };

            // OK avere toast per delete (non c'è form con AutoSaveIndicator)
            toast.success("Previsione eliminata con successo");
        });
        builder.addCase(deletePrevisione.rejected, (state, action) => {
            state.previsioniList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione della previsione");
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setPrevisioneCorrenteId,
    resetPrevisioni
} = previsioniSlice.actions;

export const previsioniSliceReducer = previsioniSlice.reducer;